// Filename: ActivityLogsPage.js
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Stack,
  Avatar,
  FormControlLabel,
  FormLabel,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import dayjs from "dayjs";
import BreadcrumbsBar from "../components/BreadcrumbsBar";
import { toast } from "react-toastify";

const ActivityLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [userType, setUserType] = useState("");
  const [compoundId, setCompoundId] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [compounds, setCompounds] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [isLoading, setLoading] = useState(false);
  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = {
        userType,
        compoundId,
        start: startDate ? startDate.toISOString().split("T")[0] : undefined,
        end: endDate ? endDate.toISOString().split("T")[0] : undefined,
      };
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/activityLogs`,
        params,
        { withCredentials: true }
      );
      setLogs(res.data);
    } catch (error) {
      toast("Fetch error");
    } finally{
      setLoading(false);
    }
  };

  const fetchCompounds = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/getCompoundList`,
        { withCredentials: true }
      );
      setCompounds(res.data.compounds);
    } catch (error) {
      toast("Fetch error");
    }
  };

  useEffect(() => {
    fetchCompounds();
    fetchLogs();
  }, [userType, compoundId, startDate, endDate]);

  const columns = [
    {
      field: "user",
      headerName: "User",
      width: 200,
      renderCell: ({ row }) => (
        <Box display="flex" mt={2} className="notranslate">
          <Stack direction="row" spacing={1} alignItems="center">
            <Box mt={2}>
              <Avatar src={row?.user?.avatar_url} />
            </Box>
            <Box>
              <Typography variant="body2">{row?.user?.FullName}</Typography>
              <Typography variant="caption" color="text.secondary">
                @{row?.user?.username}
              </Typography>
            </Box>
          </Stack>
        </Box>
      ),
    },
    { field: "action", headerName: "Action", width: 150 },
    { field: "description", headerName: "Description", width: 300 },
    {
      field: "compound",
      headerName: "Compound",
      width: 180,
      renderCell: ({ row }) => (
        <Typography className="notranslate">{row.compound?.name || "-"}</Typography>
      ),
    },
    {
      field: "createdAt",
      headerName: "Date",
      width: 180,
      renderCell: ({ row }) => (
        <Typography>
          {dayjs(row.createdAt).format("YYYY-MM-DD HH:mm")}
        </Typography>
      ),
    },
  ];

  return (
    <Box>
      <BreadcrumbsBar />
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} columns={{ xs: 2, sm: 8, md: 10 }}>
          <Grid item size={{ xs: 2, sm: 8, md: 2 }}>
            <FormControl fullWidth size="large">
              <InputLabel component="legend">User Type</InputLabel>
              <Select
                value={userType}
                onChange={(e) => setUserType(e.target.value)}
                label="User Type"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="admin">Super Admin</MenuItem>
                <MenuItem value="CMT">CMT</MenuItem>
                <MenuItem value="LANDLORD">Landlord</MenuItem>
                <MenuItem value="TENANT">Tenant</MenuItem>
                <MenuItem value="SERVICE_PROVIDER">Service Provider</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item size={{ xs: 2, sm: 8, md: 2 }}>
            <FormControl fullWidth size="large">
              <InputLabel component="legend">Compound</InputLabel>
              <Select
                value={compoundId}
                onChange={(e) => setCompoundId(e.target.value)}
                label="Compound"
                className="notranslate"
              >
                <MenuItem value="" className="notranslate">All</MenuItem>
                {compounds.map((compound) => (
                  <MenuItem className="notranslate" key={compound.id} value={compound.id}>
                    {compound.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item size={{ xs: 2, sm: 8, md: 2 }}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={setStartDate}
              format="YYYY-MM-DD"
            />
          </Grid>
          <Grid item size={{ xs: 2, sm: 8, md: 2 }}>
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={setEndDate}
              format="YYYY-MM-DD"
            />
          </Grid>
          <Grid item size={{ xs: 2, sm: 8, md: 2 }}>
            <Box display="flex" justifyContent="flex-end">
              <TextField
                label="Search"
                onChange={fetchLogs}
                sx={{ width: 150 }}
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ height: "70vh" }}>
        <DataGrid
          rows={logs}
          columns={columns}
          getRowId={(row) => row.id}
          pagination
          pageSize={pageSize}
          loading={isLoading}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10,
              },
            },
          }}
          pageSizeOptions={[5, 10, 15, 20, 25]}
          getRowHeight={() => "auto"}
          sx={{
            "& .MuiDataGrid-cell": {
              display: "flex",
              justifyContent: "left",
              alignItems: "center",
              margin: "5px",
              padding: "5px",
            },
            "& .MuiDataGrid-columnHeaders": {
              justifyContent: "center",
              textAlign: "center",
            },
          }}
        />
      </Paper>
    </Box>
  );
};

export default ActivityLogsPage;
